// apps/client/src/components/leads/CalendarConnectionCard.tsx
//
// A broker connects THEIR OWN calendar here, so lead routing can skip them
// while they are in an appointment.
//
// Self-contained on purpose: it owns its loading, error and empty states and
// takes only a companyId, so it can be dropped into the availability page with
// a one-line mount and moved later without untangling anything.
//
// The privacy line is not decoration. VALUTTEN asks Google for
// `calendar.freebusy` and nothing else, so the only thing it can ever see is
// when the broker is busy. Someone handing over their work diary deserves to
// read that in plain words before they click, not find it in a policy later.

import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  Link2,
  RefreshCw,
  ShieldCheck,
  Unlink,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import { Skeleton } from '../common/SkeletonLoader';

type ConnectionStatus =
  | 'pending_auth'
  | 'active'
  | 'reauth_required'
  | 'error'
  | 'revoked';

interface ConnectionSummary {
  uid: string;
  connectionId: string;
  accountEmail: string | null;
  status: ConnectionStatus;
  lastSyncedAt: number | null;
  consecutiveFailures: number;
}

interface StatusResponse {
  success: boolean;
  connection: ConnectionSummary | null;
  routing: { mode: 'off' | 'observe' | 'enforce'; requireConnection: boolean };
}

interface StartResponse {
  success: boolean;
  connectionId: string;
  authUrl: string;
}

interface Props {
  companyId: string;
  /** Rendered above the card, for the page to label the section if it wants. */
  className?: string;
}

/** "3 minutes ago", and never a raw epoch in front of a broker. */
function formatRelative(epochMs: number | null): string {
  if (!epochMs) return 'never';
  const deltaMs = Date.now() - epochMs;
  if (deltaMs < 60_000) return 'just now';
  const minutes = Math.floor(deltaMs / 60_000);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

export const CalendarConnectionCard: React.FC<Props> = ({ companyId, className }) => {
  const [status, setStatus] = useState<StatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    try {
      setError(null);
      const call = httpsCallable<unknown, StatusResponse>(
        functions,
        'calendarConnectionAction',
      );
      const result = await call({ action: 'status', companyId });
      setStatus(result.data);
    } catch (e) {
      // Never silent: a broker who cannot see why the card is empty will
      // assume it is connected.
      console.error('[CalendarConnectionCard] status failed', e);
      setError('Could not load your calendar connection. Try refreshing the page.');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    void load();
  }, [load]);

  const connect = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const call = httpsCallable<unknown, StartResponse>(
        functions,
        'calendarConnectionAction',
      );
      const result = await call({ action: 'start', companyId, returnTo: 'availability' });
      // Full-page navigation, not a popup: Google's consent screen blocks
      // framing, and a popup is the first thing a browser suppresses.
      window.location.assign(result.data.authUrl);
    } catch (e) {
      console.error('[CalendarConnectionCard] start failed', e);
      setError('Could not start the connection. Please try again in a moment.');
      setBusy(false);
    }
  }, [companyId]);

  const disconnect = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const call = httpsCallable(functions, 'calendarConnectionAction');
      await call({ action: 'disconnect', companyId });
      await load();
    } catch (e) {
      console.error('[CalendarConnectionCard] disconnect failed', e);
      setError('Could not disconnect the calendar. Please try again in a moment.');
    } finally {
      setBusy(false);
    }
  }, [companyId, load]);

  const connection = status?.connection ?? null;
  const routing = status?.routing;
  const isConnected = connection?.status === 'active';
  const needsReauth = connection?.status === 'reauth_required';
  const hasFaulted = connection?.status === 'error';

  return (
    <div
      className={`rounded-lg border border-gray-700 bg-gray-900/40 p-5 ${className ?? ''}`}
    >
      <div className="flex items-start justify-between gap-3 mb-1">
        <h3 className="text-base font-semibold text-white flex items-center gap-2">
          <CalendarClock className="w-4 h-4 text-blue-500" aria-hidden="true" />
          Calendar
        </h3>
        {isConnected && (
          <span className="inline-flex items-center gap-1 text-xs text-emerald-400">
            <CheckCircle2 className="w-3.5 h-3.5" aria-hidden="true" />
            Connected
          </span>
        )}
      </div>
      <p className="text-xs text-gray-400 mb-4">
        Connect your Google Calendar so new leads skip past you while you are in
        an appointment, and come to you when you are free.
      </p>

      {loading ? (
        <div className="space-y-2" aria-busy="true">
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-9 w-40 mt-3" />
        </div>
      ) : (
        <>
          {error && (
            <div className="flex items-start gap-2 rounded-md border border-red-900/60 bg-red-950/40 p-3 mb-4 text-xs text-red-300">
              <AlertTriangle className="w-4 h-4 mt-px shrink-0" aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}

          {needsReauth && (
            <div className="flex items-start gap-2 rounded-md border border-amber-900/60 bg-amber-950/40 p-3 mb-4 text-xs text-amber-300">
              <AlertTriangle className="w-4 h-4 mt-px shrink-0" aria-hidden="true" />
              <span>
                VALUTTEN has lost access to your calendar, usually because the
                permission was removed in your Google account. Reconnect to start
                it syncing again.
              </span>
            </div>
          )}

          {hasFaulted && (
            <div className="flex items-start gap-2 rounded-md border border-amber-900/60 bg-amber-950/40 p-3 mb-4 text-xs text-amber-300">
              <AlertTriangle className="w-4 h-4 mt-px shrink-0" aria-hidden="true" />
              <span>Your calendar is not syncing at the moment. Try reconnecting.</span>
            </div>
          )}

          {connection && connection.status !== 'revoked' ? (
            <dl className="text-sm space-y-2 mb-4">
              <div className="flex items-center justify-between border-b border-gray-800 pb-2">
                <dt className="text-gray-400">Account</dt>
                <dd className="text-white">{connection.accountEmail ?? 'Google Calendar'}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-gray-400">Last checked</dt>
                <dd className="text-white">{formatRelative(connection.lastSyncedAt)}</dd>
              </div>
            </dl>
          ) : (
            <p className="text-sm text-gray-300 mb-4">No calendar connected.</p>
          )}

          {/* The consequence of NOT connecting, stated where the decision is
              made rather than left for the broker to discover by missing out. */}
          {routing?.requireConnection && !isConnected && (
            <div className="flex items-start gap-2 rounded-md border border-amber-900/60 bg-amber-950/40 p-3 mb-4 text-xs text-amber-300">
              <AlertTriangle className="w-4 h-4 mt-px shrink-0" aria-hidden="true" />
              <span>
                Your business requires a connected calendar for lead routing.
                Until you connect one, new leads will not be assigned to you.
              </span>
            </div>
          )}

          {routing?.mode === 'observe' && isConnected && (
            <p className="text-xs text-gray-400 mb-4">
              Calendar routing is in trial mode. Your calendar is syncing, but it
              is not affecting who gets leads yet.
            </p>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {isConnected ? (
              <button
                type="button"
                onClick={disconnect}
                disabled={busy}
                className="inline-flex items-center gap-2 rounded-md border border-gray-700 bg-gray-800 px-3 py-2 text-xs text-gray-200 hover:bg-gray-700 disabled:opacity-50"
              >
                <Unlink className="w-3.5 h-3.5" aria-hidden="true" />
                Disconnect
              </button>
            ) : (
              <button
                type="button"
                onClick={connect}
                disabled={busy}
                className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-500 disabled:opacity-50"
              >
                <Link2 className="w-3.5 h-3.5" aria-hidden="true" />
                Connect Google Calendar
              </button>
            )}

            {(needsReauth || hasFaulted) && (
              <button
                type="button"
                onClick={connect}
                disabled={busy}
                className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-500 disabled:opacity-50"
              >
                <RefreshCw className="w-3.5 h-3.5" aria-hidden="true" />
                Reconnect
              </button>
            )}
          </div>

          <p className="mt-4 flex items-start gap-2 text-xs text-gray-400">
            <ShieldCheck className="w-3.5 h-3.5 mt-px shrink-0 text-gray-500" aria-hidden="true" />
            <span>
              VALUTTEN reads only your busy and free times. It never reads event
              titles, guests, locations or notes, and it never creates, changes
              or deletes anything in your calendar. You can disconnect at any
              time.
            </span>
          </p>
        </>
      )}
    </div>
  );
};

export default CalendarConnectionCard;
